import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import Debug from 'debug'
import throttle from 'lodash/throttle'
import { AnimatePresence, motion } from 'motion/react'
import {
  callUnaryMethod,
  createConnectQueryKey,
  useTransport,
} from '@connectrpc/connect-query'
import { useQuery } from '@tanstack/react-query'

import {
  listMonthSegments,
  listYearSegments,
} from '@jotta/grpc-connect-openapi/collectionQuery'
import { Order } from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/photo/v2/collection.v2_pb'
import {
  useDebounce,
  useDocumentEventListener,
  useHeightResizeObserver,
} from '@jotta/hooks'
import SvgFillCaretUpDown from '@jotta/ui/SvgFillCaretUpDown'
import { bisectLeft } from '@jotta/utils/bisect'
import { clamp } from '@jotta/utils/number'

import type { CollectionRef } from '../../../api/useCollection'

const debug = Debug('scrubber')

export interface ScrubberSegment {
  from: Date
  to: Date
}

class ScrubberHelper {
  constructor(public readonly segments: ScrubberSegment[] = []) {}

  public getDateFromOffset(offset: number): dayjs.Dayjs {
    const segmentCount = this.segments.length
    const i = clamp(0, Math.floor(offset * segmentCount), segmentCount - 1)
    const { from, to } = this.segments[i]
    const frac = 1 - (offset * segmentCount - i)
    return dayjs.utc(from.valueOf() + frac * (to.valueOf() - from.valueOf()))
  }

  public getSelectableDateFromOffset(
    offset: number,
    segmentData: ReturnType<typeof useSegments>['data'],
  ) {
    if (!segmentData) {
      return undefined
    }
    const { segments, segmentKeys } = segmentData
    const firstSegment = segments.at(0)
    if (!firstSegment) {
      return undefined
    }
    const lastestDate = firstSegment.to
    const offsetDate = this.getDateFromOffset(offset)
    if (offsetDate.isAfter(lastestDate)) {
      return dayjs.utc(lastestDate)
    }
    let initialMonth = offsetDate.month()
    const currentYear = new Date().getFullYear()
    for (let year = offsetDate.year(); year <= currentYear; year++) {
      for (let monthIndex = initialMonth; monthIndex < 12; monthIndex++) {
        const value = segmentKey(year, monthIndex)
        if (segmentKeys.has(value)) {
          return dayjs.utc(Date.UTC(year, monthIndex)).endOf('month')
        }
      }
      initialMonth = 0
    }
  }

  public getOffsetFromDate(date: dayjs.Dayjs): number {
    let i = bisectLeft(
      this.segments,
      seg => date.valueOf() - seg.from.valueOf(),
    )

    i = Math.min(Math.max(i, 0), this.segments.length - 1)

    if (i <= 0) {
      return 0
    }

    const { from, to } = this.segments[i]
    const diff = Math.max(0, date.valueOf() - from.valueOf())
    const frac = 1 - diff / (to.valueOf() - from.valueOf())

    return (frac + i) / this.segments.length
  }

  public get gridTemplateRows(): string {
    return this.segments.map(seg => `1fr`).join(' ')
  }
}

function formatSegmentsToHeight(
  height: number,
  segments: ScrubberSegment[] | undefined,
  minSegmentSpacing = 60,
): ScrubberSegment[] | undefined {
  if (!segments) {
    return
  }

  const formatted = segments.slice()

  if (formatted.length <= 2 || height / formatted.length >= minSegmentSpacing) {
    return formatted
  }

  const removeSegment = (index: number) => {
    const [seg] = formatted.splice(index, 1)
    formatted[index].to = seg.to
  }

  const num = Math.max(2, Math.floor(height / minSegmentSpacing))

  const toRemove = segments.length - num
  let i = formatted.length - 2
  for (let removed = 0; removed < toRemove; removed++) {
    removeSegment(i)
    i = (i > 2 ? i : formatted.length) - 2
  }

  return formatted
}

function useHeightFormattedSegments(
  element: HTMLElement | null | undefined,
  segments: ScrubberSegment[] | undefined,
  minSegmentSpacing = 60,
) {
  const [data, setData] = useState<ScrubberSegment[] | undefined>()

  const format = useCallback(
    (height?: number) => {
      if (!element) {
        return
      }

      if (typeof height === 'undefined') {
        height = element.getBoundingClientRect().height
      }

      const result = formatSegmentsToHeight(height, segments, minSegmentSpacing)
      setData(result)

      return result
    },
    [element, segments, minSegmentSpacing],
  )

  useEffect(() => {
    format()
  }, [format])

  useHeightResizeObserver(element, format, 100)

  return { format, data }
}

function useSegments(collectionRef: CollectionRef) {
  const transport = useTransport()
  return useQuery({
    queryKey: createConnectQueryKey({
      schema: listYearSegments,
      cardinality: undefined,
      input: {
        collectionRef,
      },
    }),
    queryFn: async ({ signal }) => {
      const listYearSegmentsResponse = await callUnaryMethod(
        transport,
        listYearSegments,
        {
          collectionRef,
        },
        {
          signal,
        },
      )

      // TODO might be worth trying to split this up into subqueries even if it is not crystal clear it will produce better rendering performance
      // In any case, make sure to return a stable result because the height calculation depends on a stable segment list
      const monthSegments = await Promise.all(
        listYearSegmentsResponse.years.map(async ({ year }) => {
          const { months } = await callUnaryMethod(
            transport,
            listMonthSegments,
            {
              collectionRef,
              year,
            },
            {
              signal,
            },
          )

          return months.map(({ month }) => {
            const monthIndex = month.valueOf() - 1
            return {
              from: new Date(Date.UTC(year, monthIndex, 1)),
              to: new Date(Date.UTC(year, monthIndex + 1, 0, 23, 59, 59, 999)),
            } satisfies ScrubberSegment
          })
        }),
      )

      const segments = monthSegments.flatMap(value => value)
      const now = new Date()
      const yearSegments = listYearSegmentsResponse.years.map(
        ({ year }) =>
          ({
            from: new Date(Date.UTC(year, 0, 1)),
            to:
              year === now.getFullYear()
                ? now
                : new Date(Date.UTC(year + 1, 0, 0, 23, 59, 59, 999)),
          }) satisfies ScrubberSegment,
      )
      const segmentKeys = new Set(
        segments.map(({ from }) =>
          segmentKey(from.getFullYear(), from.getMonth()),
        ),
      )
      return {
        segments,
        segmentKeys,
        yearSegments,
      }
    },
  })
}

function segmentKey(year: number, month: number) {
  return `${year}-${month}`
}

export function ScrubberV2({
  collectionRef,
  onSelect,
  scrollingElement,
  indicatorDate,
  onScrubbing,
  autohide = false,
}: {
  collectionRef: CollectionRef
  onSelect: (timestamp: Dayjs) => void
  indicatorDate?: number
  scrollingElement?: Element | Document | null
  onScrubbing: (isScrubbing: boolean) => void
  autohide?: boolean
}) {
  const scrubberRef = useRef<HTMLDivElement>(null)
  const cursorRef = useRef<HTMLDivElement>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)
  const labelRef = useRef<HTMLDivElement>(null)
  const mobileThumbRef = useRef<HTMLDivElement>(null)
  const divThumbLabel = useRef<HTMLDivElement>(null)

  const { data: realSegments } = useSegments(collectionRef)

  const [isScrolling, setIsScrolling] = useState(false)
  const [isScrubbing, _setIsScrubbing] = useState(false)

  const setIsScrollingDelayed = useDebounce(setIsScrolling, 1500)

  const setIsScrubbing = useCallback(
    (state: boolean) => {
      _setIsScrubbing(state)
      onScrubbing(state)
      setIsScrollingDelayed(state)

      if (state) {
        setIsScrollingDelayed.flush()
      }
    },
    [setIsScrollingDelayed, onScrubbing],
  )

  useEffect(() => {
    if (!scrollingElement) {
      return
    }

    const handleScroll = throttle(() => {
      setIsScrollingDelayed(true)
      setIsScrollingDelayed.flush()

      if (!isScrubbing) {
        setIsScrollingDelayed(false)
      }
    }, 200)

    scrollingElement.addEventListener('scroll', handleScroll)
    handleScroll()
    handleScroll.flush()

    return () => {
      handleScroll.cancel()
      scrollingElement.removeEventListener('scroll', handleScroll)
    }
  }, [scrollingElement, setIsScrollingDelayed, isScrubbing])

  const { data: segments } = useHeightFormattedSegments(
    scrubberRef.current,
    realSegments?.yearSegments,
  )

  // Initial text
  useEffect(() => {
    const firstSegment = segments?.at(0)
    if (labelRef.current && !labelRef.current.innerText && firstSegment) {
      const date = dayjs.utc(firstSegment.to)
      labelRef.current.innerText = date.format('MMM YYYY')
    }
  }, [segments])

  const helper = useMemo(() => new ScrubberHelper(segments), [segments])

  // Move indicator and label when indicator prop changes
  useEffect(() => {
    if (
      isScrubbing ||
      !segments ||
      !indicatorDate ||
      !scrubberRef.current ||
      !indicatorRef.current ||
      !labelRef.current ||
      !cursorRef.current
    ) {
      return
    }

    const day = indicatorDate ? dayjs.utc(indicatorDate) : dayjs.utc()

    const offset = Math.round(
      helper.getOffsetFromDate(day) * scrubberRef.current.clientHeight,
    )

    if (!scrubberRef.current.matches(':hover')) {
      cursorRef.current.style.display = 'none'
      labelRef.current.style.top = `${offset}px`
      labelRef.current.innerText = day.format('MMM YYYY')
    }

    indicatorRef.current.style.top = `${offset}px`

    if (mobileThumbRef.current) {
      mobileThumbRef.current.style.top = `${offset}px`
    }
    if (divThumbLabel.current) {
      divThumbLabel.current.innerText = dayjs.utc(indicatorDate).format('LL')
    }
  }, [indicatorDate, segments, helper, isScrubbing])

  function onMove(clientY: number) {
    if (
      !isScrubbing ||
      !scrubberRef.current ||
      !cursorRef.current ||
      !labelRef.current ||
      !mobileThumbRef.current
    ) {
      return
    }

    const { top, height } = scrubberRef.current.getBoundingClientRect()
    const offset = Math.round(Math.min(Math.max(clientY - top, 0), height))

    cursorRef.current.style.display = ''
    cursorRef.current.style.top = `${offset}px`
    labelRef.current.style.top = `${offset}px`
    mobileThumbRef.current.style.top = `${offset}px`
    const date = helper.getDateFromOffset(
      offset / scrubberRef.current.clientHeight,
    )
    labelRef.current.innerText = date.format('MMM YYYY')

    if (divThumbLabel.current) {
      divThumbLabel.current.innerText = date.format('MMM YYYY')
    }
  }

  function selectFromScrubberPosition(clientY: number) {
    setIsScrubbing(false)
    if (scrubberRef.current) {
      const { top, height } = scrubberRef.current.getBoundingClientRect()
      const clickedDate = helper.getSelectableDateFromOffset(
        (clientY - top) / height,
        realSegments,
      )
      debug(`selecting: ${clickedDate}`)
      if (clickedDate) {
        onSelect(clickedDate)
      }
    }
  }

  useDocumentEventListener('mousemove', ev => onMove(ev.clientY), {
    enabled: isScrubbing,
  })
  useDocumentEventListener(
    'mouseup',
    ev => selectFromScrubberPosition(ev.clientY),
    {
      enabled: isScrubbing,
    },
  )

  if (!realSegments?.segments?.length) {
    return undefined
  }

  return (
    <div
      className="group relative z-timelineScrubber h-full cursor-pointer select-none py-6 lg:w-4 lg:py-4"
      data-testid="ScrubberV2"
      data-scrolling={isScrolling}
      data-scrubbing={isScrubbing}
      onMouseMove={e => {
        if (scrubberRef.current) {
          const { top, height } = scrubberRef.current.getBoundingClientRect()
          const offset = Math.round(
            Math.min(Math.max(e.clientY - top, 0), height),
          )

          if (cursorRef.current && labelRef.current) {
            cursorRef.current.style.display = ''
            cursorRef.current.style.top = `${offset}px`
            labelRef.current.style.top = `${offset}px`
            const date = helper.getSelectableDateFromOffset(
              offset / scrubberRef.current.clientHeight,
              realSegments,
            )
            labelRef.current.innerText = date ? date.format('MMM YYYY') : ''
          }
        }
      }}
      onMouseUp={e => selectFromScrubberPosition(e.clientY)}
    >
      <div className="absolute bottom-0 right-0 top-0 w-0 bg-grey1 lg:w-4" />
      <div
        ref={scrubberRef}
        data-show={!autohide || isScrolling}
        data-scrolling={isScrolling}
        style={{
          gridTemplateRows: helper.gridTemplateRows,
        }}
        className="relative grid h-full auto-cols-[1fr] opacity-0 transition-all duration-300 data-[show=true]:opacity-100 hover:opacity-100"
      >
        <span className="pointer-events-none absolute right-[5px] top-[-3px] hidden size-[6px] rounded-full bg-grey6 lg:block"></span>

        <AnimatePresence initial={false}>
          {segments &&
            segments.map((seg, i) => (
              <motion.div
                layout="size"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key={i}
                className="hidden items-end justify-end pr-[5px] lg:flex"
              >
                <div className="group pointer-events-none flex translate-y-1/2 items-center gap-2">
                  <span className="size-[6px] rounded-full bg-grey6" />
                </div>
              </motion.div>
            ))}
        </AnimatePresence>

        <div
          ref={indicatorRef}
          className="pointer-events-none absolute right-[5px] hidden size-[6px] -translate-y-1/2 rounded-full bg-informative-fg transition-all duration-300 lg:block"
        >
          <div className="pointer-events-none absolute right-7 top-[3px] -translate-y-1/2 whitespace-nowrap rounded-full bg-informative px-3 text-base font-medium leading-7 text-informative opacity-0 transition-all duration-300 group-data-[scrolling=true]:opacity-100">
            {dayjs.utc(indicatorDate).format('LL')}
          </div>
        </div>
        <div
          ref={cursorRef}
          className="pointer-events-none absolute right-0 hidden h-[4px] w-4 -translate-y-1/2 opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:transition-none group-hover:transition-opacity lg:block"
        >
          <div className="absolute left-0 w-1 border-2 border-informative" />
          <div className="absolute right-0 w-1 border-2 border-informative" />
          <div className="absolute right-[3px] top-[-3px] size-[10px] rounded-full border-2 border-informative" />
        </div>
        <div
          ref={labelRef}
          className="pointer-events-none absolute right-8 hidden -translate-y-1/2 whitespace-nowrap rounded-md border border-[var(--color-light-gray)] bg-surface-container-lowest px-3 text-base font-medium leading-7 text-informative opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:transition-opacity lg:block"
        />

        <div
          ref={mobileThumbRef}
          className="absolute right-0 flex h-11 w-8 -translate-y-1/2 touch-none items-center justify-start rounded-l-xl bg-btn-primary text-btn-primary-text opacity-0 group-data-[scrolling=true]:opacity-100 lg:hidden"
          onMouseDown={() => setIsScrubbing(true)}
          onTouchStart={() => setIsScrubbing(true)}
          onTouchMove={e => onMove(e.targetTouches[0].clientY)}
          onTouchEnd={e =>
            selectFromScrubberPosition(e.changedTouches[0].clientY)
          }
          onContextMenu={e => e.preventDefault()}
        >
          <SvgFillCaretUpDown width={30} />
          <div className="pointer-events-auto absolute -bottom-3 -left-3 -top-3 right-0 hidden group-data-[scrolling=true]:block" />
          <div
            ref={divThumbLabel}
            className="pointer-events-none absolute right-11 whitespace-nowrap rounded-3xl bg-black px-3 py-2 text-base font-bold text-white opacity-80 transition-all duration-300 group-data-[scrubbing=true]:right-20 [&:empty]:hidden"
          />
        </div>
      </div>
    </div>
  )
}
